import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

export const AGENDA_VIEWS = ["day", "week", "month"] as const;
export type AgendaView = (typeof AGENDA_VIEWS)[number];

const WEEK_OPTIONS = { weekStartsOn: 1 } as const;
const DATE_PARAM = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Unknown or missing `?view=` falls back to the day view (the old agenda). */
export const parseAgendaView = (value: string | null): AgendaView =>
  AGENDA_VIEWS.includes(value as AgendaView) ? (value as AgendaView) : "day";

/** `?date=YYYY-MM-DD` as a local civil day; anything else means today. */
export const parseAgendaDate = (
  value: string | null,
  today: Date = new Date(),
): Date => {
  const match = value ? DATE_PARAM.exec(value) : null;
  if (!match) return startOfDay(today);
  const [year, month, day] = [match[1], match[2], match[3]].map(Number);
  const date = new Date(year, month - 1, day);
  // Reject overflow such as 2026-02-31 instead of silently rolling over.
  if (!isValid(date) || date.getMonth() !== month - 1) {
    return startOfDay(today);
  }
  return date;
};

export const formatAgendaDate = (date: Date): string =>
  format(date, "yyyy-MM-dd");

/** Days the view shows, first to last (inclusive). Month pads to whole weeks. */
export const agendaRange = (
  view: AgendaView,
  date: Date,
): { start: Date; end: Date } => {
  if (view === "day") return { start: startOfDay(date), end: startOfDay(date) };
  if (view === "week") {
    return {
      start: startOfWeek(date, WEEK_OPTIONS),
      end: startOfDay(endOfWeek(date, WEEK_OPTIONS)),
    };
  }
  return {
    start: startOfWeek(startOfMonth(date), WEEK_OPTIONS),
    end: startOfDay(endOfWeek(endOfMonth(date), WEEK_OPTIONS)),
  };
};

export const agendaDays = (view: AgendaView, date: Date): Date[] =>
  eachDayOfInterval(agendaRange(view, date));

/** Prev / next step: one day, one week or one month. */
export const shiftAgendaDate = (
  view: AgendaView,
  date: Date,
  direction: 1 | -1,
): Date => {
  if (view === "day") return addDays(date, direction);
  if (view === "week") return addDays(date, 7 * direction);
  // date-fns clamps Jan 31 + 1 month to Feb 28, which is what we want.
  return addMonths(date, direction);
};

/** Bucket items by local day key, keeping each bucket in input order. */
export const groupByDay = <T>(
  items: readonly T[],
  keyOf: (item: T) => string,
): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    map.set(key, [...(map.get(key) ?? []), item]);
  }
  return map;
};
